<script setup lang="ts">
/**
 * « Qui est prévenu, et comment » sur la fiche d'une surveillance
 * (`tableauCanauxLecture` S:5533-5546), en lecture.
 *
 * Ce que l'utilisateur doit vivre : il lit qui est prévenu, par SMS, par mail
 * ou les deux, si le numéro de chacun permet de recevoir un SMS, et, au clic,
 * ce que sont devenus ses derniers messages de test. Un employé ne voit jamais
 * un numéro : le masque ne s'affiche que si la matrice ouvre
 * `voir_numero_membre`, et la façade ne le sert qu'au propriétaire et à
 * l'administrateur. Un destinataire absent des membres reste sans nom, dit
 * comme tel. Une liste non servie ne se lit jamais « personne ».
 *
 * Lot F6 : sur chaque ligne, « Envoyer un message de test » (gestes 35 et
 * 36, `PanneauMessageTest`) et, pour un numéro absent ou à corriger, « Saisir
 * le numéro » ou « Corriger le numéro » (geste 34, `DialogueNumero`).
 */
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import { useAcces } from "@modules/alarmes-v2/acces/useAcces"
import { useOuvertureStore } from "@modules/alarmes-v2/stores/ouverture.store"
import type { StatutMembres } from "@modules/alarmes-v2/stores/membres.store"
import type {
    AlarmesV2Destinataire,
    AlarmesV2Membre,
    AlarmesV2Personne,
} from "@modules/alarmes-v2/types/alarmes-v2"
import { raisonDeNonEnvoi } from "@modules/alarmes-v2/utils/journal"
import { lignesPrevenus } from "@modules/alarmes-v2/utils/prevenus"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()
const tr = t as unknown as Traduire

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** La surveillance. */
        idInstance: string
        /** `destinataires` servi ; `undefined` ou `null` : non servi. */
        destinataires?: AlarmesV2Destinataire[] | null
        /** Les membres lus, par identifiant. */
        membres?: Record<string, AlarmesV2Membre>
        /** Où en est la lecture des membres. */
        statutMembres?: StatutMembres
        /** Les noms servis avec la surveillance. */
        personnes?: Record<string, AlarmesV2Personne>
    }>(),
    {
        destinataires: undefined,
        membres: () => ({}),
        statutMembres: "attente",
        personnes: () => ({}),
    }
)

const { acces } = useAcces()
const ouverture = useOuvertureStore()

// ================================
// COMPUTED
// ================================
/** Le masque du numéro ne se montre que si la matrice l'ouvre. */
const getVoitLeNumero = computed(() => acces("voir_numero_membre").ok)

/** Le masque du numéro d'un destinataire, si la matrice permet de le voir. */
function masqueDe(idUser: string): string {
    if (!getVoitLeNumero.value) return ""
    const m = props.membres[idUser]?.phone_masque
    return typeof m === "string" ? m : ""
}

const getLignes = computed(() =>
    Array.isArray(props.destinataires)
        ? lignesPrevenus(
              props.destinataires,
              props.membres,
              props.statutMembres,
              props.personnes,
              getVoitLeNumero.value,
              tr
          )
        : []
)

/**
 * Ce que la section montre. Une liste servie vide dit « personne » ; une liste
 * non servie, ou dont aucune ligne n'est lisible, n'est pas « personne ».
 */
const getVue = computed((): "non_disponible" | "personne" | "lignes" => {
    if (!Array.isArray(props.destinataires)) return "non_disponible"
    if (props.destinataires.length === 0) return "personne"
    return getLignes.value.length > 0 ? "lignes" : "non_disponible"
})

/**
 * Pourquoi un SMS coché ne part pas (offre sans SMS, solde épuisé), ou `""`.
 * Un solde inconnu ne ferme rien.
 */
const getRaisonSms = computed((): string => {
    const raison = ouverture.offre?.sms?.raison_indisponible
    return raison ? raisonDeNonEnvoi(raison, "sms", tr) : ""
})
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <section class="flex flex-col gap-2" data-test="qui-est-prevenu">
        <h2 class="text-sm font-semibold text-surface-900">
            {{ t("alarmesV2.prevenus.titre") }}
        </h2>
        <p
            v-if="getVue === 'non_disponible'"
            class="text-sm text-surface-600"
            data-test="prevenus-non-disponible"
        >
            {{ t("alarmesV2.prevenus.nonDisponible") }}
        </p>
        <p
            v-else-if="getVue === 'personne'"
            class="text-sm text-red-800"
            data-test="personne-prevenue"
        >
            {{ t("alarmesV2.prevenus.personneNEstPrevenu") }}
        </p>
        <template v-else>
            <p
                v-if="statutMembres === 'erreur'"
                class="text-xs text-surface-600"
                data-test="membres-indisponibles"
            >
                {{ t("alarmesV2.prevenus.membresIndisponibles") }}
            </p>
            <p
                v-else-if="
                    statutMembres === 'chargement' ||
                    statutMembres === 'attente'
                "
                class="text-xs text-surface-600"
            >
                {{ t("alarmesV2.prevenus.chargementMembres") }}
            </p>
            <div class="overflow-x-auto">
                <table class="w-full text-sm">
                    <thead>
                        <tr class="text-left text-xs text-surface-600">
                            <th scope="col" class="py-1 pr-3 font-medium">
                                {{ t("alarmesV2.prevenus.personne") }}
                            </th>
                            <th scope="col" class="py-1 pr-3 font-medium">
                                {{ t("alarmesV2.prevenus.sms") }}
                            </th>
                            <th scope="col" class="py-1 font-medium">
                                {{ t("alarmesV2.prevenus.mail") }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="l in getLignes"
                            :key="l.idUser"
                            class="border-t border-surface-200 align-top"
                            data-test="prevenu"
                        >
                            <th
                                scope="row"
                                class="py-2 pr-3 text-left font-normal"
                            >
                                <span
                                    class="block"
                                    :class="
                                        l.horsDesMembres
                                            ? 'italic text-surface-700'
                                            : 'text-surface-900'
                                    "
                                    data-test="prevenu-nom"
                                    >{{ l.nom }}</span
                                >
                                <span
                                    class="block text-xs text-surface-600"
                                    data-test="prevenu-numero"
                                    >{{
                                        [l.role, l.numero]
                                            .filter((x) => x)
                                            .join(" · ")
                                    }}</span
                                >
                                <ResultatTest
                                    class="mt-1"
                                    :id-instance="idInstance"
                                    :id-user="l.idUser"
                                />
                                <PanneauMessageTest
                                    :id-instance="idInstance"
                                    :ligne="l"
                                    :masque="masqueDe(l.idUser)"
                                />
                                <DialogueNumero
                                    v-if="!l.horsDesMembres"
                                    :id-user="l.idUser"
                                    :nom="l.nom"
                                    :etat-numero="l.etatNumero"
                                />
                            </th>
                            <td class="py-2 pr-3" data-test="prevenu-sms">
                                <template v-if="l.sms">
                                    <span v-if="getRaisonSms">
                                        {{
                                            t(
                                                "alarmesV2.prevenus.cocheNonEnvoye"
                                            )
                                        }}
                                        <span
                                            class="block text-xs text-surface-600"
                                            >{{ getRaisonSms }}</span
                                        >
                                    </span>
                                    <span v-else>{{
                                        t("alarmesV2.prevenus.oui")
                                    }}</span>
                                </template>
                                <span
                                    v-else-if="l.etatNumero === 'sans_numero'"
                                    class="text-surface-600"
                                    >{{
                                        t("alarmesV2.prevenus.sansNumero")
                                    }}</span
                                >
                                <span v-else class="text-surface-600">{{
                                    t("alarmesV2.prevenus.non")
                                }}</span>
                            </td>
                            <td class="py-2" data-test="prevenu-mail">
                                {{
                                    l.mail
                                        ? t("alarmesV2.prevenus.oui")
                                        : t("alarmesV2.prevenus.non")
                                }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </section>
</template>
